// DocumentList.jsx - COMPLETE VERSION with redirect fix only
import React, { useEffect, useState, useContext } from "react";
import UploadDocumentForm from "./UploadDocumentForm";
import MigrationButton from "../MigrationButton";
import { getApiBaseURL, secureAxios } from "../../utils/api";
import { AuthContext } from "../../contexts/AuthContext";
import { useSearchParams } from "react-router-dom";
import { useLocation } from "react-router-dom";
import { auditApi } from "../../utils/api";
import { toast } from "react-toastify";

const DocumentList = () => {
  // All original state variables
  const [activeTab, setActiveTab] = useState("view");
  const [documents, setDocuments] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [authNumberFilter, setAuthNumberFilter] = useState("");
  const [consumerFilter, setConsumerFilter] = useState("");
  const [userFilter, setUserFilter] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [selectedDocs, setSelectedDocs] = useState([]);
  const [emailModalVisible, setEmailModalVisible] = useState(false);
  const [searchAuth, setSearchAuth] = useState("");
  const [refreshKey, setRefreshKey] = useState(0);
  const [loadingDocs, setLoadingDocs] = useState(false);
  const apiBase = getApiBaseURL();

  // Counselor email features state
  const [counselorSuggestions, setCounselorSuggestions] = useState([]);
  const [loadingCounselors, setLoadingCounselors] = useState(false);
  const [emailTemplates, setEmailTemplates] = useState({});
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [showCounselorHelp, setShowCounselorHelp] = useState(false);
  const [emailToValue, setEmailToValue] = useState("");
  const [emailSubjectValue, setEmailSubjectValue] = useState("Document Bundle");
  const [emailBodyValue, setEmailBodyValue] = useState(
    "Please find the attached documents."
  );

  // Multiple recipients and CC state
  const [ccUser, setCcUser] = useState(false);
  const [additionalEmails, setAdditionalEmails] = useState("");

  // Admin CC state
  const [tenantAdmins, setTenantAdmins] = useState([]);
  const [selectedAdminCCs, setSelectedAdminCCs] = useState([]);
  const [loadingAdmins, setLoadingAdmins] = useState(false);

  // Context and params
  const { tenant_id, user } = useContext(AuthContext);

  const [searchParams] = useSearchParams();
  const consumer_id = searchParams.get("consumer_id");
  const authorization_id = searchParams.get("authorization_id");
  const location = useLocation();
  const isAdmin = user?.role?.toLowerCase() === "admin";
  const vendorTenantId = searchParams.get("tenant_id");
  const isVendor = user?.tenant_type?.toLowerCase() === "vendor";

  // Clear filters function
  const clearFilters = () => {
    setSearchTerm("");
    setAuthNumberFilter("");
    setConsumerFilter("");
    setUserFilter("");
    setStartDate("");
    setEndDate("");
    setSearchAuth("");
  };

  // Helper function to process email lists
  const processEmailList = (emailString) => {
    if (!emailString) return [];
    return emailString
      .split(/[,;]/) // Split by comma or semicolon
      .map((email) => email.trim())
      .filter((email) => email && email.includes("@"));
  };

  // Helper function to clean up document names for display
  const cleanDocumentName = (docName) => {
    if (!docName) return "Document";
    
    // Remove file extension
    let name = docName.replace(/\.(pdf|doc|docx|xls|xlsx|png|jpg|jpeg)$/i, "");
    
    // Remove UUID patterns (8-4-4-4-12 format)
    name = name.replace(/_?[a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12}/gi, "");
    
    // Remove timestamp patterns (13+ digit numbers)
    name = name.replace(/_?\d{13,}/g, "");
    
    // Remove trailing underscores or hyphens
    name = name.replace(/[_-]+$/g, "");
    
    // Replace underscores with spaces
    name = name.replace(/_/g, " ");
    
    // Clean up multiple spaces
    name = name.replace(/\s+/g, " ").trim();
    
    return name || "Document";
  };

  // Get counselor suggestions when documents are selected
  const getCounselorSuggestions = async (documentIds) => {
    if (!documentIds || documentIds.length === 0) {
      setCounselorSuggestions([]);
      setLoadingCounselors(false);
      return;
    }

    setLoadingCounselors(true);

    try {
      const response = await secureAxios.post(
        "/api/documents/get-counselor-emails",
        {
          document_ids: documentIds,
          tenant_id: tenant_id,
        }
      );

      const data = response.data;
      const counselors = data.counselors || [];

      setCounselorSuggestions(counselors);

      // Auto-suggest primary counselor email and fetch templates
      if (data.primary_suggestion && !emailToValue) {
        setEmailToValue(data.primary_suggestion.email);
        
        // Also fetch templates for the auto-suggested email
        getEmailTemplates(documentIds, data.primary_suggestion.email);

        toast.info(
          `Suggested recipient: ${data.primary_suggestion.name} - ${data.primary_suggestion.reason}`,
          {
            autoClose: 4000,
          }
        );
      } else if (data.primary_suggestion && emailToValue) {
        // Email already set but we have a suggestion, fetch templates with suggestion email
        getEmailTemplates(documentIds, data.primary_suggestion.email);
      } else if (emailToValue) {
        // If email already set but no suggestion, fetch templates with existing email
        getEmailTemplates(documentIds, emailToValue);
      } else if (counselors.length > 0 && counselors[0].email) {
        // No primary suggestion and no email set, but we have counselors - use first one
        getEmailTemplates(documentIds, counselors[0].email);
      }

      // Auto-populate RSA email in Additional Recipients if available
      if (counselors.length > 0) {
        // Collect unique RSA emails from all suggested counselors
        const rsaEmails = counselors
          .filter((c) => c.rsa_email)
          .map((c) => c.rsa_email);
        const uniqueRsaEmails = [...new Set(rsaEmails)];
        
        if (uniqueRsaEmails.length > 0) {
          setAdditionalEmails(uniqueRsaEmails.join(", "));
        }
      }
    } catch (error) {
      if (error.response?.status === 400) {
        toast.error("Invalid request parameters for counselor suggestions");
      } else if (error.response?.status === 500) {
        toast.error("Server error loading counselor suggestions");
      } else {
        toast.error("Failed to load counselor suggestions");
      }

      setCounselorSuggestions([]);
    } finally {
      setLoadingCounselors(false);
    }
  };

  // Fetch admins in the tenant for CC options
  const fetchTenantAdmins = async () => {
    if (!tenant_id) return;
    
    setLoadingAdmins(true);
    try {
      const response = await secureAxios.get(`/api/users`, {
        params: { tenant_id }
      });
      
      // Filter to only admins, excluding current user
      const admins = (response.data || []).filter(
        (u) => u.role?.toLowerCase() === "admin" && u.user_id !== user?.user_id && u.email
      );
      
      setTenantAdmins(admins);
    } catch (error) {
      console.error("Failed to fetch tenant admins:", error);
      setTenantAdmins([]);
    } finally {
      setLoadingAdmins(false);
    }
  };

  // Get email templates for selected documents and counselor
  const getEmailTemplates = async (documentIds, counselorEmail) => {
    if (!documentIds || documentIds.length === 0 || !counselorEmail) {
      console.log("getEmailTemplates: Skipping - missing documentIds or counselorEmail", { documentIds, counselorEmail });
      return;
    }

    console.log("getEmailTemplates: Fetching templates for", { documentIds, counselorEmail });

    try {
      const response = await secureAxios.post(
        "/api/documents/suggest-email-content",
        {
          document_ids: documentIds,
          counselor_email: counselorEmail,
          tenant_id: tenant_id,
        }
      );

      const data = response.data;
      console.log("getEmailTemplates: Received templates", data.suggestions);
      setEmailTemplates(data.suggestions || {});

      // Auto-fill first subject suggestion
      if (
        data.suggestions?.subject_options &&
        data.suggestions.subject_options.length > 0
      ) {
        setEmailSubjectValue(data.suggestions.subject_options[0]);
      }
    } catch (error) {
      console.error("getEmailTemplates: Error fetching templates", error);
      // Silently fail for template loading
    }
  };

  // Apply email template
  const applyEmailTemplate = (template) => {
    setSelectedTemplate(template);
    setEmailBodyValue(template.content);

    toast.success(`Applied ${template.name} template`);
  };

  // Quick fill counselor email
  const fillCounselorEmail = (counselor) => {
    setEmailToValue(counselor.email);

    // Auto-populate RSA email in Additional Recipients if available
    if (counselor.rsa_email) {
      setAdditionalEmails(counselor.rsa_email);
    }

    // Get templates for this counselor
    getEmailTemplates(selectedDocs, counselor.email);

    toast.success(`Selected ${counselor.name} as recipient`);
  };

  // Enhanced fetchDocs with better error handling (NO REDIRECTS)
  const fetchDocs = async () => {
    setLoadingDocs(true);
    try {
      console.log("DocumentList: Starting fetchDocs with context:", {
        tenant_id,
        consumer_id,
        authorization_id,
        user_tenant_type: user?.tenant_type,
        user_role: user?.role,
        user_counselor_id: user?.counselor_id,
      });

      const requestParams = {
        tenant_id: tenant_id,
        consumer_id,
        authorization_id,
      };

      console.log(
        " DocumentList: Making API request to /api/documents with params:",
        requestParams
      );

      const res = await secureAxios.get("/api/documents", {
        params: requestParams,
      });

      console.log("DocumentList: API response received:", {
        status: res.status,
        documentCount: res.data?.length || 0,
        documents: res.data,
      });

      setDocuments(res.data);

      // Enhanced audit logging with customer-vendor context
      await auditApi.logActionSafe("VIEW_DOCUMENT_LIST", "DOCUMENT", null, {
        total_documents: res.data.length,
        filters_applied: {
          consumer_id,
          authorization_id,
        },
        user_context: {
          tenant_type: user?.tenant_type,
          role: user?.role,
          counselor_id: user?.counselor_id,
        },
        access_model: "customer_vendor_cross_tenant",
      });

      // If no documents returned, log for debugging
      if (res.data.length === 0) {
        console.log(
          "ÃƒÂ¢Ã…Â¡Ã‚Â ÃƒÂ¯Ã‚Â¸Ã‚Â DocumentList: No documents returned - this may be normal if no documents exist"
        );

        // Show informational message instead of error
        toast.info("No documents found for this authorization.", {
          autoClose: 3000,
        });
      }
    } catch (err) {
      console.error("ÃƒÂ¢Ã‚ÂÃ…â€™ DocumentList: Error fetching documents:", {
        status: err.response?.status,
        statusText: err.response?.statusText,
        data: err.response?.data,
        message: err.message,
        request_params: {
          tenant_id,
          consumer_id,
          authorization_id,
        },
        user_context: {
          tenant_type: user?.tenant_type,
          role: user?.role,
        },
      });

      //  CRITICAL FIX: DO NOT REDIRECT - SHOW ERROR MESSAGES INSTEAD
      if (err.response?.status === 404) {
        console.warn("ÃƒÂ¢Ã…Â¡Ã‚Â ÃƒÂ¯Ã‚Â¸Ã‚Â Documents endpoint or authorization not found");
        toast.error("Authorization not found or no documents available.", {
          autoClose: 5000,
        });
      } else if (err.response?.status === 403) {
        //  ENHANCED 403 handling for customer-vendor access
        const errorDetails = err.response?.data;

        if (errorDetails?.code === "HIPAA_ACCESS_DENIED") {
          console.error("ÃƒÂ°Ã…Â¸Ã¢â‚¬ÂÃ¢â‚¬â„¢ HIPAA Access Denied:", errorDetails);

          // Show detailed error message based on user type
          let errorMessage = "Access denied to these documents.";

          if (errorDetails.context) {
            const { your_role, your_tenant_type, access_rules } =
              errorDetails.context;

            if (your_tenant_type === "vendor" && your_role === "job coach") {
              errorMessage =
                "Job coaches can only view documents for consumers they are assigned to. Please check your consumer assignments.";
            } else if (your_tenant_type === "vendor" && your_role !== "admin") {
              errorMessage =
                "Only vendor admins can view all documents. Job coaches can only view documents for assigned consumers.";
            } else if (
              your_tenant_type === "customer" &&
              your_role === "counselor"
            ) {
              errorMessage =
                "Counselors can only view documents for consumers they are assigned to. Please check your consumer assignments.";
            } else {
              errorMessage = `${
                errorDetails.details ||
                "You do not have permission to view these documents."
              }\n\nAccess Rules:\n${access_rules?.vendor_users || ""}\n${
                access_rules?.customer_users || ""
              }`;
            }
          }

          toast.error(errorMessage, {
            autoClose: 8000,
            style: { whiteSpace: "pre-line" },
          });
        } else {
          // Generic 403 handling
          toast.error(
            "Access denied to documents. You may not have permission to view documents for this authorization.",
            { autoClose: 5000 }
          );
        }
      } else if (err.response?.status === 401) {
        console.warn("ÃƒÂ°Ã…Â¸Ã¢â‚¬ÂÃ‚Â Authentication required");
        toast.error("Your session has expired. Please log in again.", {
          autoClose: 3000,
        });
        // Don't redirect here - let secureAxios handle it
      } else if (err.response?.status === 500) {
        console.error("ÃƒÂ°Ã…Â¸Ã¢â‚¬â„¢Ã‚Â¥ Server error");
        toast.error(
          "Server error while loading documents. Please try again or contact support.",
          { autoClose: 5000 }
        );
      } else if (err.code === "ERR_NETWORK" || err.response?.status === 0) {
        console.error("ÃƒÂ°Ã…Â¸Ã…â€™Ã‚Â Network error");
        toast.error(
          "Network connection failed. Please check your internet connection and try again.",
          { autoClose: 5000 }
        );
      } else {
        // Generic error handling
        console.error("Unknown error:", err);
        toast.error(
          `Failed to load documents: ${
            err.response?.data?.error || err.message || "Unknown error"
          }`,
          { autoClose: 5000 }
        );
      }

      // CRITICAL: Set empty array instead of redirecting
      setDocuments([]);
    } finally {
      setLoadingDocs(false);
    }
  };

  useEffect(() => {
    if (tenant_id) {
      fetchDocs();
    }
  }, [consumer_id, authorization_id, tenant_id, refreshKey]);

  // Update counselor suggestions when selected documents change
  useEffect(() => {
    if (selectedDocs.length > 0 && emailModalVisible) {
      getCounselorSuggestions(selectedDocs);
    }
  }, [selectedDocs, emailModalVisible]);

  // Always fetch templates when modal opens with selected docs
  useEffect(() => {
    if (emailModalVisible && selectedDocs.length > 0) {
      // Fetch templates with any available email
      const emailToUse = emailToValue || counselorSuggestions[0]?.email || "";
      if (emailToUse) {
        console.log("Modal opened - fetching templates with email:", emailToUse);
        getEmailTemplates(selectedDocs, emailToUse);
      } else {
        // No email yet, fetch templates with a placeholder - backend should still return templates
        console.log("Modal opened - fetching templates without specific email");
        getEmailTemplates(selectedDocs, "placeholder@email.com");
      }
    }
  }, [emailModalVisible]);

  // Fetch tenant admins when modal opens (for CC options)
  useEffect(() => {
    if (emailModalVisible && isAdmin) {
      fetchTenantAdmins();
      // Reset selected admin CCs when modal opens
      setSelectedAdminCCs([]);
    }
  }, [emailModalVisible, isAdmin]);

  // Check if action requires PHI authorization
  const requiresPHIAuth = (doc) => {
    return doc.consumer_id || doc.contains_phi;
  };

  // Handle download function
  const handleDownload = async (id) => {
    try {
      const doc = documents.find((d) => d.document_id === id);
      await auditApi.logActionSafe("DOWNLOAD_DOCUMENT", "DOCUMENT", id, {
        document_name: doc?.document_name,
        consumer_id: doc?.consumer_id,
        contains_phi: requiresPHIAuth(doc),
        purpose_of_use: "operations",
      });

      const downloadRes = await secureAxios.get(
        `/api/documents/download/${id}`,
        {
          params: { tenant_id },
          responseType: "blob",
        }
      );

      const url = window.URL.createObjectURL(new Blob([downloadRes.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", doc?.document_name || "document");
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      toast.error("Failed to download document");
    }
  };

  // Extract filename function
  const extractFilename = (filePath) => {
    const parts = filePath?.split("/");
    return parts?.length ? parts[parts.length - 1] : "";
  };

  // Handle preview function
  const handlePreview = async (doc) => {
    try {
      await auditApi.logActionSafe(
        "PREVIEW_DOCUMENT",
        "DOCUMENT",
        doc.document_id,
        {
          document_name: doc.document_name,
          consumer_id: doc.consumer_id,
          contains_phi: requiresPHIAuth(doc),
          purpose_of_use: "operations",
        }
      );

      const res = await secureAxios.get(
        `/api/documents/preview/id/${doc.document_id}`,
        {
          params: { tenant_id },
        }
      );

      if (res.data?.signed_url) {
        window.open(res.data.signed_url, "_blank");
      } else {
        alert("Preview URL not available");
      }
    } catch (err) {
      if (err.response?.status === 404) {
        alert("Document not found");
      } else if (err.response?.status === 403) {
        alert("Access denied to this document");
      } else {
        alert("Failed to generate preview link");
      }
    }
  };

  // Handle email documents with counselor suggestions
  const handleEmailDocuments = async () => {
    if (selectedDocs.length === 0) return;

    setEmailModalVisible(true);
    setShowCounselorHelp(true);

    // Reset form values
    setEmailToValue("");
    setEmailSubjectValue("Document Bundle");
    setEmailBodyValue("Please find the attached documents.");
    setSelectedTemplate(null);
    setEmailTemplates([]);
    setCcUser(false);
    setAdditionalEmails("");

    // Get counselor suggestions
    await getCounselorSuggestions(selectedDocs);
  };

  // Send email bundle with multiple recipients and CC support
  const sendEmailBundle = async () => {
    const email = emailToValue;
    const subject = emailSubjectValue;
    const body = emailBodyValue;
    const deliveryMethod =
      document.querySelector('input[name="deliveryMethod"]:checked')?.value ||
      "attachments";

    if (!email || !subject || !body) {
      toast.error("Please fill in all fields");
      return;
    }

    // Process multiple email addresses
    const primaryEmails = processEmailList(email);
    const additionalEmailList = additionalEmails
      ? processEmailList(additionalEmails)
      : [];

    // Add user to CC if requested
    const ccEmails = [];
    if (ccUser && user?.email) {
      ccEmails.push(user.email);
    }

    // Add selected admin emails to CC
    if (selectedAdminCCs.length > 0) {
      const adminEmails = tenantAdmins
        .filter((admin) => selectedAdminCCs.includes(admin.user_id))
        .map((admin) => admin.email)
        .filter((email) => email);
      ccEmails.push(...adminEmails);
    }

    if (primaryEmails.length === 0) {
      toast.error("Please enter at least one valid email address");
      return;
    }

    const totalRecipients =
      primaryEmails.length + additionalEmailList.length + ccEmails.length;

    try {
      const selectedDocuments = documents.filter((d) =>
        selectedDocs.includes(d.document_id)
      );

      // Enhanced audit logging
      await auditApi.logActionSafe(
        "EMAIL_DOCUMENTS",
        "DOCUMENT",
        selectedDocs.join(","),
        {
          recipient_emails: [...primaryEmails, ...additionalEmailList].join(
            ", "
          ),
          cc_emails: ccEmails.join(", "),
          sender_email: user?.email,
          document_count: selectedDocs.length,
          document_ids: selectedDocs,
          contains_phi: selectedDocuments.some((d) => requiresPHIAuth(d)),
          purpose_of_use: "operations",
          counselor_suggested: counselorSuggestions.length > 0,
          delivery_method: deliveryMethod,
          cc_user: ccUser,
          cc_admin_count: selectedAdminCCs.length,
          total_recipients: totalRecipients,
        }
      );

      // Send request with multiple recipients and CC
      const response = await secureAxios.post("/api/documents/email-bundle", {
        document_ids: selectedDocs,
        to_email: primaryEmails[0], // Primary recipient
        additional_emails: additionalEmailList,
        cc_emails: ccEmails,
        subject,
        body,
        tenant_id: tenant_id,
        delivery_method: deliveryMethod,
      });

      // Enhanced success message with details
      const details = response.data?.details;

      if (details) {
        if (deliveryMethod === "secure_links") {
          toast.success(
            `Secure download links sent to ${
              details.totalRecipients || totalRecipients
            } recipient(s)! 
            ${details.documentCount} documents available 
            Links expire in ${details.linkExpiry}`,
            { autoClose: 6000 }
          );
        } else {
          toast.success(
            `Email sent successfully to ${
              details.totalRecipients || totalRecipients
            } recipient(s)! 
            ${details.attachmentCount} attachments 
            ${details.totalSizeMB}MB total`,
            { autoClose: 6000 }
          );
        }
      } else {
        toast.success(
          `Document bundle sent successfully to ${totalRecipients} recipient(s)!`
        );
      }

      setEmailModalVisible(false);
      setSelectedDocs([]);
      setCounselorSuggestions([]);
      setEmailTemplates([]);
      setShowCounselorHelp(false);
      setCcUser(false);
      setAdditionalEmails("");
    } catch (err) {
      // Enhanced error messages
      if (err.response?.data?.code === "ATTACHMENTS_TOO_LARGE") {
        toast.error(
          `ÃƒÂ¢Ã‚ÂÃ…â€™ Files too large: ${err.response.data.total_size_mb}MB (limit: ${err.response.data.limit_mb}MB)`
        );
      } else if (err.response?.data?.code === "EMAIL_SERVICE_ERROR") {
        toast.error(
          "ÃƒÂ¢Ã‚ÂÃ…â€™ Email service error. Please try again in a few minutes."
        );
      } else {
        toast.error("ÃƒÂ¢Ã‚ÂÃ…â€™ Failed to send email bundle. Please try again.");
      }
    }
  };

  // Close modal and reset all state including new fields
  const closeEmailModal = () => {
    setEmailModalVisible(false);
    setCounselorSuggestions([]);
    setEmailTemplates([]);
    setShowCounselorHelp(false);
    setSelectedTemplate(null);
    setCcUser(false);
    setAdditionalEmails("");
    setEmailToValue("");
    setEmailSubjectValue("Document Bundle");
    setEmailBodyValue("Please find the attached documents.");
  };

  // Better migration completion handler with database refresh
  const handleMigrationComplete = async (documentId, newProvider) => {
    // Update local state immediately for UI responsiveness
    setDocuments((prev) =>
      prev.map((doc) =>
        doc.document_id === documentId
          ? {
              ...doc,
              storage_provider: newProvider,
              migration_status: "completed",
              migrated_at: new Date().toISOString(),
            }
          : doc
      )
    );

    // Force refresh from database to ensure accuracy
    setTimeout(() => {
      setRefreshKey((prev) => prev + 1);
    }, 1000);

    toast.success("Document migrated successfully!");
  };

  // Storage provider badge with better logic
  const getStorageProviderBadge = (document) => {
    const provider = document.storage_provider;
    const migrationStatus = document.migration_status;

    if (provider === "google" || migrationStatus === "completed") {
      return (
        <span className="badge bg-success">
          <i className="fas fa-cloud me-1" />
          Google Cloud
        </span>
      );
    } else if (provider === "aws" || !provider) {
      return (
        <span className="badge bg-secondary">
          <i className="fab fa-aws me-1" />
          AWS S3
        </span>
      );
    } else {
      return (
        <span className="badge bg-warning">
          <i className="fas fa-question me-1" />
          {provider || "Unknown"}
        </span>
      );
    }
  };

  // Filtered documents logic
  const filteredDocuments = documents
    .filter((doc) =>
      doc.notes?.toLowerCase().includes(searchTerm.toLowerCase())
    )
    .filter((doc) =>
      consumerFilter
        ? doc.consumer_name
            ?.toLowerCase()
            .includes(consumerFilter.toLowerCase())
        : true
    )
    .filter((doc) =>
      userFilter
        ? doc.user_name?.toLowerCase().includes(userFilter.toLowerCase())
        : true
    )
    .filter((doc) =>
      authNumberFilter
        ? doc.authorization_number
            ?.toLowerCase()
            .includes(authNumberFilter.toLowerCase())
        : true
    )
    .filter((doc) => {
      // Date range filter
      if (!startDate && !endDate) return true;

      const docDate = doc.upload_date ? new Date(doc.upload_date) : null;
      if (!docDate) return true;

      // Get just the date portion as YYYY-MM-DD string for reliable comparison
      const docDateStr = docDate.toISOString().split("T")[0];

      if (startDate && endDate) {
        return docDateStr >= startDate && docDateStr <= endDate;
      } else if (startDate) {
        return docDateStr >= startDate;
      } else if (endDate) {
        return docDateStr <= endDate;
      }
      return true;
    });

  return (
    <div className="container-fluid mt-4">
      <h1>Document Library</h1>

      {/* Tab Navigation */}
      <ul className="nav nav-tabs mb-3">
        <li className="nav-item">
          <button
            className={`nav-link ${activeTab === "view" ? "active" : ""}`}
            onClick={() => setActiveTab("view")}
          >
            View Documents
          </button>
        </li>
        <li className="nav-item">
          <button
            className={`nav-link ${activeTab === "upload" ? "active" : ""}`}
            onClick={() => setActiveTab("upload")}
          >
            Upload Document
          </button>
        </li>
      </ul>

      {/* Tab Content */}
      {activeTab === "view" && (
        <div>
          {/* Filters - Responsive Layout */}
          <div className="row g-2 mb-3">
            <div className="col-12 col-sm-6 col-lg-4">
              <input
                type="text"
                className="form-control"
                placeholder="Search by Note"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            <div className="col-12 col-sm-6 col-lg-4">
              <input
                type="text"
                className="form-control"
                placeholder="Filter by Authorization Number"
                value={authNumberFilter}
                onChange={(e) => setAuthNumberFilter(e.target.value)}
              />
            </div>
            <div className="col-12 col-sm-6 col-lg-4">
              <input
                type="text"
                className="form-control"
                placeholder="Filter by Consumer Name"
                value={consumerFilter}
                onChange={(e) => setConsumerFilter(e.target.value)}
              />
            </div>
            <div className="col-12 col-sm-6 col-lg-3">
              <input
                type="text"
                className="form-control"
                placeholder="Filter by User Name"
                value={userFilter}
                onChange={(e) => setUserFilter(e.target.value)}
              />
            </div>
            <div className="col-6 col-sm-4 col-lg-2">
              <input
                type="date"
                className="form-control"
                placeholder="Start Date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                title="Start Date"
              />
            </div>
            <div className="col-6 col-sm-4 col-lg-2">
              <input
                type="date"
                className="form-control"
                placeholder="End Date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                title="End Date"
              />
            </div>
            <div className="col-12 col-sm-4 col-lg-auto">
              <button
                className="btn btn-outline-secondary w-100"
                onClick={clearFilters}
              >
                Clear Filters
              </button>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="d-flex flex-wrap gap-2 mb-3">
            <button
              className="btn btn-primary"
              onClick={handleEmailDocuments}
              disabled={selectedDocs.length === 0}
            >
              Email Selected ({selectedDocs.length})
            </button>
            <button
              className="btn btn-outline-info"
              onClick={() => setRefreshKey((prev) => prev + 1)}
              title="Refresh document list from database"
              disabled={loadingDocs}
            >
              {loadingDocs ? (
                <>
                  <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
                  Loading...
                </>
              ) : (
                <>
                  <i className="fas fa-sync-alt me-1" />
                  Refresh
                </>
              )}
            </button>
          </div>

          {/* Documents Table - Responsive */}
          <div className="table-responsive">
            <table className="table table-striped table-hover align-middle">
              <thead className="table-dark">
                <tr>
                  <th style={{ width: "50px" }}>Select</th>
                  <th className="d-none d-md-table-cell">Uploaded By</th>
                  <th>Consumer</th>
                  <th className="d-none d-sm-table-cell">Auth #</th>
                  <th className="d-none d-lg-table-cell">Upload Date</th>
                  <th className="d-none d-xl-table-cell">Note</th>
                  <th>Storage</th>
                  <th style={{ width: "100px" }}>Preview</th>
                </tr>
              </thead>
              <tbody>
                {filteredDocuments.length === 0 ? (
                  <tr>
                    <td colSpan="8" className="text-center py-4">
                      No documents found
                    </td>
                  </tr>
                ) : (
                  filteredDocuments.map((doc) => (
                    <tr key={doc.document_id}>
                      <td>
                        <input
                          type="checkbox"
                          className="form-check-input"
                          checked={selectedDocs.includes(doc.document_id)}
                          onChange={() => {
                            if (selectedDocs.includes(doc.document_id)) {
                              setSelectedDocs(
                                selectedDocs.filter(
                                  (id) => id !== doc.document_id
                                )
                              );
                            } else {
                              setSelectedDocs([
                                ...selectedDocs,
                                doc.document_id,
                              ]);
                            }
                          }}
                        />
                      </td>
                      <td className="d-none d-md-table-cell">
                        <span
                          className="text-truncate d-inline-block"
                          style={{ maxWidth: "150px" }}
                        >
                          {doc.uploaded_by_name || "Unknown User"}
                        </span>
                      </td>
                      <td>
                        <div>
                          <span
                            className="text-truncate d-inline-block"
                            style={{ maxWidth: "150px" }}
                          >
                            {doc.consumer_name || "Unknown Consumer"}
                          </span>
                          {/* Show extra info on mobile */}
                          <div className="d-md-none small text-muted">
                            {doc.uploaded_by_name || "Unknown User"}
                          </div>
                          <div className="d-sm-none small text-muted">
                            {doc.authorization_number || "-"}
                          </div>
                        </div>
                      </td>
                      <td className="d-none d-sm-table-cell">
                        {doc.authorization_number || "-"}
                      </td>
                      <td className="d-none d-lg-table-cell">
                        {new Date(doc.upload_date).toLocaleDateString()}
                      </td>
                      <td className="d-none d-xl-table-cell">
                        <span
                          className="text-truncate d-inline-block"
                          style={{ maxWidth: "200px" }}
                        >
                          {doc.notes || "No Note"}
                        </span>
                      </td>
                      <td>
                        <div className="d-flex flex-column align-items-start gap-1">
                          {getStorageProviderBadge(doc)}
                          <MigrationButton
                            document={doc}
                            onMigrationComplete={handleMigrationComplete}
                          />
                        </div>
                      </td>
                      <td>
                        <button
                          className="btn btn-secondary btn-sm"
                          onClick={() => handlePreview(doc)}
                        >
                          Preview
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
              <tfoot>
                <tr>
                  <td colSpan="8" className="text-muted text-center small">
                    <i className="bi bi-info-circle me-1" />
                    Showing {filteredDocuments.length} document(s). All access
                    is logged for HIPAA compliance.
                    <br />
                    <small>
                      Last refresh: {new Date().toLocaleTimeString()}
                    </small>
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      )}

      {/* Upload tab */}
      {activeTab === "upload" && (
        <UploadDocumentForm
          onUploadSuccess={() => {
            setActiveTab("view"); // Switch to view tab
            setRefreshKey((prev) => prev + 1); // Refresh the document list
          }}
        />
      )}

      {/* Email Modal with Multiple Recipients & CC Support */}
      {emailModalVisible && (
        <div
          className="modal d-block"
          tabIndex="-1"
          style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
        >
          <div className="modal-dialog modal-xl">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  <i className="fas fa-envelope me-2" />
                  Email Document Bundle ({selectedDocs.length} documents)
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={closeEmailModal}
                ></button>
              </div>

              <div className="modal-body">
                {/* Sender Information */}
                <div className="alert alert-info mb-3">
                  <strong>
                    <i className="fas fa-user me-1" />
                    Sending as:
                  </strong>{" "}
                  {user?.name} ({user?.email})
                  <br />
                  <small>
                    Recipients will see this information in the email
                  </small>
                </div>

                {/* Delivery Method Selection */}
                <div className="card mb-3">
                  <div className="card-header bg-primary text-white">
                    <h6 className="mb-0">
                      <i className="fas fa-shipping-fast me-1" />
                      Delivery Method
                    </h6>
                  </div>
                  <div className="card-body">
                    <div className="row">
                      <div className="col-md-6">
                        <div className="form-check">
                          <input
                            className="form-check-input"
                            type="radio"
                            name="deliveryMethod"
                            id="attachments"
                            value="attachments"
                            defaultChecked
                          />
                          <label
                            className="form-check-label"
                            htmlFor="attachments"
                          >
                            <strong>
                              <i className="fas fa-paperclip me-1" />
                              Traditional Attachments
                            </strong>
                            <br />
                            <small className="text-muted">
                              Files attached directly to email (may show PDF
                              icons)
                            </small>
                          </label>
                        </div>
                      </div>
                      <div className="col-md-6">
                        <div className="form-check">
                          <input
                            className="form-check-input"
                            type="radio"
                            name="deliveryMethod"
                            id="secure_links"
                            value="secure_links"
                          />
                          <label
                            className="form-check-label"
                            htmlFor="secure_links"
                          >
                            <strong>
                              <i className="fas fa-link me-1" />
                              Secure Download Links
                            </strong>
                            <br />
                            <small className="text-muted">
                              Clean email with secure download links (no
                              attachment icons)
                            </small>
                          </label>
                        </div>
                      </div>
                    </div>
                    <div className="alert alert-light mt-2 mb-0">
                      <small>
                        <strong>
                          <i className="fas fa-lightbulb text-warning me-1" />
                          Tip:
                        </strong>{" "}
                        Use secure links for cleaner emails without attachment
                        icons. Links expire automatically (24 hours for PHI, 7
                        days for general documents).
                      </small>
                    </div>
                  </div>
                </div>

                {/* Loading State */}
                {loadingCounselors && (
                  <div className="alert alert-secondary mb-3">
                    <div className="d-flex align-items-center">
                      <div
                        className="spinner-border spinner-border-sm me-2"
                        role="status"
                      >
                        <span className="visually-hidden">Loading...</span>
                      </div>
                      <span>Loading counselor suggestions...</span>
                    </div>
                  </div>
                )}

                {/* Counselor Suggestions */}
                {counselorSuggestions.length > 0 && (
                  <div className="card mb-3">
                    <div className="card-header bg-success text-white">
                      <h6 className="mb-0">
                        <i className="fas fa-user-friends me-1" />
                        Suggested Recipients (Counselors)
                      </h6>
                    </div>
                    <div className="card-body">
                      <p className="text-muted mb-2">
                        Based on the selected documents, these counselors work
                        with the consumers:
                      </p>
                      <div className="d-flex flex-wrap gap-2">
                        {counselorSuggestions.map((counselor, index) => (
                          <button
                            key={index}
                            className={`btn btn-outline-success btn-sm ${
                              emailToValue === counselor.email ? "active" : ""
                            }`}
                            onClick={() => fillCounselorEmail(counselor)}
                            title={`${
                              counselor.consumers?.length || 0
                            } consumers, ${counselor.document_count} documents`}
                          >
                            <i className="fas fa-user me-1" />
                            {counselor.name}
                            <br />
                            <small>{counselor.email}</small>
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                )}

                {/* Enhanced Email Recipients Section */}
                <div className="card mb-3">
                  <div className="card-header bg-warning text-dark">
                    <h6 className="mb-0">
                      <i className="fas fa-at me-1" />
                      Email Recipients
                    </h6>
                  </div>
                  <div className="card-body">
                    <div className="row">
                      {/* Primary Email */}
                      <div className="col-md-6">
                        <label htmlFor="bundleEmail" className="form-label">
                          <i className="fas fa-envelope me-1" />
                          Primary Recipient *
                          {counselorSuggestions.length > 0 && (
                            <small className="text-muted ms-2">
                              (Auto-suggested counselor)
                            </small>
                          )}
                        </label>
                        <input
                          type="email"
                          className="form-control"
                          id="bundleEmail"
                          value={emailToValue}
                          onChange={(e) => {
                            setEmailToValue(e.target.value);
                            // Get templates when email changes
                            if (e.target.value && selectedDocs.length > 0) {
                              getEmailTemplates(selectedDocs, e.target.value);
                            }
                          }}
                          placeholder="primary@example.com"
                          required
                        />
                        {counselorSuggestions.length > 0 && (
                          <small className="form-text text-success">
                            Counselor email auto-suggested based on documents
                          </small>
                        )}
                      </div>

                      {/* Additional Recipients */}
                      <div className="col-md-6">
                        <label
                          htmlFor="additionalEmails"
                          className="form-label"
                        >
                          <i className="fas fa-user-plus me-1" />
                          Additional Recipients
                          <small className="text-muted ms-2">(Optional)</small>
                        </label>
                        <input
                          type="text"
                          className="form-control"
                          id="additionalEmails"
                          value={additionalEmails}
                          onChange={(e) => setAdditionalEmails(e.target.value)}
                          placeholder="email1@example.com, email2@example.com"
                        />
                        <small className="form-text text-muted">
                          <i className="fas fa-info-circle text-muted me-1" />
                          Separate multiple emails with commas or semicolons
                        </small>
                      </div>
                    </div>

                    {/* CC Options */}
                    <div className="row mt-3">
                      <div className="col-12">
                        <div className="form-check">
                          <input
                            className="form-check-input"
                            type="checkbox"
                            id="ccUser"
                            checked={ccUser}
                            onChange={(e) => setCcUser(e.target.checked)}
                          />
                          <label className="form-check-label" htmlFor="ccUser">
                            <strong>
                              <i className="fas fa-copy me-1" />
                              CC myself
                            </strong>{" "}
                            ({user?.email})
                            <br />
                            <small className="text-muted">
                              Get a copy of the email for your records
                            </small>
                          </label>
                        </div>
                      </div>
                    </div>

                    {/* CC Other Admins (only visible to admins) */}
                    {isAdmin && tenantAdmins.length > 0 && (
                      <div className="row mt-3">
                        <div className="col-12">
                          <label className="form-label">
                            <i className="fas fa-user-shield me-1" />
                            <strong>CC Other Admins</strong>
                            <small className="text-muted ms-2">
                              (Optional - select admins to copy)
                            </small>
                          </label>
                          <div className="border rounded p-2 bg-light" style={{ maxHeight: "180px", overflowY: "auto" }}>
                            {loadingAdmins ? (
                              <div className="text-center text-muted py-2">
                                <i className="fas fa-spinner fa-spin me-1" />
                                Loading admins...
                              </div>
                            ) : (
                              tenantAdmins.map((admin) => (
                                <div 
                                  key={admin.user_id} 
                                  className={`d-flex align-items-center p-2 rounded mb-1 ${
                                    selectedAdminCCs.includes(admin.user_id) 
                                      ? "bg-primary bg-opacity-10 border border-primary" 
                                      : "bg-white border"
                                  }`}
                                  style={{ cursor: "pointer" }}
                                  onClick={() => {
                                    if (selectedAdminCCs.includes(admin.user_id)) {
                                      setSelectedAdminCCs(
                                        selectedAdminCCs.filter((id) => id !== admin.user_id)
                                      );
                                    } else {
                                      setSelectedAdminCCs([...selectedAdminCCs, admin.user_id]);
                                    }
                                  }}
                                >
                                  <input
                                    className="form-check-input me-3"
                                    type="checkbox"
                                    id={`ccAdmin-${admin.user_id}`}
                                    checked={selectedAdminCCs.includes(admin.user_id)}
                                    onChange={() => {}}
                                    style={{ 
                                      width: "1.25rem", 
                                      height: "1.25rem",
                                      cursor: "pointer"
                                    }}
                                  />
                                  <label
                                    htmlFor={`ccAdmin-${admin.user_id}`}
                                    style={{ cursor: "pointer", marginBottom: 0 }}
                                  >
                                    <strong>{admin.name}</strong>
                                    <span className="text-muted ms-2">({admin.email})</span>
                                  </label>
                                </div>
                              ))
                            )}
                          </div>
                          {selectedAdminCCs.length > 0 && (
                            <small className="text-success mt-1 d-block">
                              <i className="fas fa-check me-1" />
                              {selectedAdminCCs.length} admin(s) will be CC'd
                            </small>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Recipients Preview */}
                    {(emailToValue || additionalEmails || ccUser || selectedAdminCCs.length > 0) && (
                      <div className="alert alert-light mt-3 mb-0">
                        <h6>
                          <i className="fas fa-paper-plane me-1" />
                          Email will be sent to:
                        </h6>
                        <ul className="mb-0">
                          {emailToValue && (
                            <li>
                              <strong>Primary:</strong> {emailToValue}
                            </li>
                          )}
                          {additionalEmails &&
                            processEmailList(additionalEmails).map(
                              (email, index) => (
                                <li key={index}>
                                  <strong>Additional:</strong> {email}
                                </li>
                              )
                            )}
                          {ccUser && user?.email && (
                            <li>
                              <strong>CC:</strong> {user?.email} (yourself)
                            </li>
                          )}
                          {selectedAdminCCs.length > 0 &&
                            tenantAdmins
                              .filter((admin) => selectedAdminCCs.includes(admin.user_id))
                              .map((admin) => (
                                <li key={admin.user_id}>
                                  <strong>CC (Admin):</strong> {admin.name} ({admin.email})
                                </li>
                              ))}
                        </ul>
                        <small className="text-muted">
                          Total:{" "}
                          {(emailToValue ? 1 : 0) +
                            processEmailList(additionalEmails).length +
                            (ccUser ? 1 : 0) +
                            selectedAdminCCs.length}{" "}
                          recipient(s)
                        </small>
                      </div>
                    )}
                  </div>
                </div>

                {/* Subject Field */}
                <div className="mb-3">
                  <label htmlFor="bundleSubject" className="form-label">
                    <i className="fas fa-heading me-1" />
                    Subject *
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    id="bundleSubject"
                    value={emailSubjectValue}
                    onChange={(e) => setEmailSubjectValue(e.target.value)}
                    placeholder="Email subject"
                    required
                  />
                </div>

                {/* Email Templates */}
                {emailTemplates.body_templates &&
                  emailTemplates.body_templates.length > 0 && (
                    <div className="mb-3">
                      <label className="form-label">
                        <i className="fas fa-magic me-1" />
                        Quick Templates
                      </label>
                      <div className="btn-group d-block" role="group">
                        {emailTemplates.body_templates.map(
                          (template, index) => (
                            <button
                              key={index}
                              type="button"
                              className={`btn btn-outline-primary btn-sm me-2 mb-2 ${
                                selectedTemplate?.name === template.name
                                  ? "active"
                                  : ""
                              }`}
                              onClick={() => applyEmailTemplate(template)}
                            >
                              {template.name}
                            </button>
                          )
                        )}
                      </div>
                    </div>
                  )}

                {/* Message Body */}
                <div className="mb-3">
                  <label htmlFor="bundleBody" className="form-label">
                    <i className="fas fa-comment-alt me-1" />
                    Message *
                  </label>
                  <textarea
                    className="form-control"
                    id="bundleBody"
                    rows="6"
                    value={emailBodyValue}
                    onChange={(e) => setEmailBodyValue(e.target.value)}
                    placeholder="Enter your message here..."
                    required
                  ></textarea>
                </div>

                {/* Document Summary */}
                <div className="alert alert-light">
                  <h6>
                    <i className="fas fa-file-alt me-1" />
                    Documents to be sent:
                  </h6>
                  <ul className="mb-0">
                    {documents
                      .filter((doc) => selectedDocs.includes(doc.document_id))
                      .map((doc) => (
                        <li key={doc.document_id}>
                          <strong>
                            {cleanDocumentName(doc.document_name)}
                            {doc.notes && ` (${doc.notes})`}
                          </strong>
                          {doc.consumer_name && (
                            <span className="text-muted">
                              {" "}
                              - {doc.consumer_name}
                            </span>
                          )}
                        </li>
                      ))}
                  </ul>
                </div>
              </div>

              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={closeEmailModal}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={sendEmailBundle}
                  disabled={
                    !emailToValue || !emailSubjectValue || !emailBodyValue
                  }
                >
                  <i className="fas fa-paper-plane me-1" />
                  Send Email
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DocumentList;